import { useState, useEffect } from "react";

import {
  Form,
  Input,
  Button,
  Upload,
  Checkbox,
  Typography,
  Card,
  Avatar,
  message,
  Divider,
  Select,
  Row,
  Col,
  DatePicker,
  Steps,
  Radio
} from "antd";
import {
  UserOutlined,
  BookOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const options = [
  { label: 'Apple', value: 'Apple' },
  { label: 'Pear', value: 'Pear' },
];
// =====================
// STRIPE CONFIGURATION
// =====================
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

if (!stripePublishableKey) {
  console.error("Missing REACT_APP_STRIPE_PUBLISHABLE_KEY");
}

// =====================
// CARD ELEMENT STYLES
// =====================
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      fontFamily: "'Outfit', sans-serif",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

// =====================
// REGISTRATION FORM WITH PAYMENT
// =====================
const RegisterFormWithPayment = ({ tierInfo }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const stripe = useStripe();
  const elements = useElements();
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [registrationData, setRegistrationData] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState("");
  const [availableTiers, setAvailableTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(tierInfo);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const heardAbout = Form.useWatch("howHeard", form);
  const needsReferralEmail = /friend|colleague/i.test(heardAbout || "");

  const locationTypes = ["urban", "suburban", "rural"];
  const genderIdentities = [
    "male",
    "female",
    "non-binary",
    "prefer-not-to-say",
    "other",
  ];
  const ethnicities = [
    "asian",
    "black",
    "hispanic",
    "white",
    "indigenous",
    "multiple",
    "prefer-not-to-say",
  ];
  const howHeardOptions = [
    "Social Media",
    "Friend/Colleague",
    "Online Search",
    "Podcast",
    "Advert",
    "Other",
  ];

  const colorTheme = {
    border: "#e2e8f0",
    textLight: "#64748b",
  };
    console.log("Tier Info",tierInfo);
  useEffect(() => {
    if (!needsReferralEmail && form.getFieldValue("referralEmail")) {
      form.setFieldsValue({ referralEmail: undefined });
    }
  }, [form, needsReferralEmail]);
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch("/api/roles/fetch");
        const data = await response.json();
        if (data.success) {
          const userOnly = (data.data || []).filter(
            (role) => role.key?.toLowerCase() === "user"
          );
          setRoles(userOnly);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await fetch("/api/membership/fetch?limit=20");
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          // Filter out internal/admin tiers never shown to users
          const HIDDEN_KEYS = ["visitor", "admin", "guest"];
          const activeTiers = data.data.filter(
            (tier) =>
              tier.isActive !== false &&
              !HIDDEN_KEYS.includes(tier.key?.toLowerCase())
          );
          setAvailableTiers(activeTiers);
          if (!tierInfo?.id) {
            const defaultTier =
              activeTiers.find((tier) => tier.key?.toLowerCase() === "free") ||
              activeTiers[0];
            setSelectedTier(defaultTier);
          }
        }
      } catch (error) {
        console.error("Fetch membership tiers:", error);
      } finally {
        setLoadingTiers(false);
      }
    };

    fetchTiers();
  }, [tierInfo]);

  const handleImageUpload = (info) => {
    const { fileList } = info;
    if (fileList.length > 0) {
      const file = fileList[0].originFileObj;
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setProfileImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : "");
  };

  // Step 1: Initialize registration (for paid) or complete registration (for free)
  const activeTier = selectedTier || tierInfo || {
    key: "free",
    title: "Free Membership",
    points: [],
    monthlyPrice: 0,
    yearlyPrice: 0,
  };

  const handleRegistration = async (values) => {
    setLoading(true);

    try {
      const membershipTierKey = activeTier.key || "free";

      if (activeTier.monthlyPrice > 0) {
        // Paid tier - initialize registration to get clientSecret
        const formData = new FormData();
        formData.append("email", values.email);
        formData.append("membershipTierKey", membershipTierKey);
        if (needsReferralEmail) {
          formData.append("referralEmail", values.referralEmail || "");
        }

        const response = await fetch(
          "/api/subscriptions/initialize-registration",
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (data.success && data.data.clientSecret) {
          setRegistrationData({
            formValues: values,
            clientSecret: data.data.clientSecret,
          });
          setFormError("");
          setCurrentStep(1);
          setLoading(false);
        } else {
          setFormError(data.message || "Registration initialization failed. Please try again.");
          setLoading(false);
        }
      } else {
        // Free tier - complete registration immediately
        const formData = new FormData();
        formData.append("fullName", values.fullName || "");
        formData.append("preferredName", values.preferredName || "");
        formData.append("email", values.email);
        formData.append("password", values.password);
        formData.append("howHeard", values.howHeard || "");
        formData.append("referralEmail", values.referralEmail || "");
        formData.append("hopesForJoining", values.hopesForJoining || "");
        formData.append("membershipTierKey", membershipTierKey);
        formData.append("primaryRoleKey", membershipTierKey);
        formData.append("billingCycle", "monthly");

        if (profileImageFile) {
          formData.append("pfp", profileImageFile);
        }

        const demographic = {
          dateOfBirth: values.dateOfBirth
            ? values.dateOfBirth.format("YYYY-MM-DD")
            : "",
          locationType: values.locationType || "",
          genderIdentity: values.genderIdentity || "",
          ethnicity: values.ethnicity || "",
          urmIdentification: !!values.urmIdentification,
        };
        formData.append("demographic", JSON.stringify(demographic));

        const response = await fetch(
          "/api/subscriptions/complete-registration",
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (data.success) {
          setFormError("");
          setFormSuccess(data.message || "Registration successful! Welcome to LWBL.");
          setTimeout(() => navigate("/thankyou"), 1500);
        } else {
          setFormError(data.message || "Registration failed. Please try again.");
          setLoading(false);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "An unexpected error occurred. Please try again.";
      setFormError(msg);
      setLoading(false);
    }
  };

  // Step 2: Process payment and complete registration
  const handlePayment = async () => {
    if (!stripe || !elements) {
      message.error("Stripe is not ready");
      return;
    }

    if (!cardComplete) {
      message.error("Please complete your card details");
      return;
    }

    setLoading(true);

    try {
      const cardElement = elements.getElement(CardElement);
      const values = registrationData.formValues;

      // Create payment method from card element
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: values.preferredName,
            email: values.email,
          },
        });

      if (pmError) {
        setFormError(pmError.message || "Payment method creation failed. Please check your card details.");
        setLoading(false);
        return;
      }

      // Confirm setup intent with payment method
      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
        registrationData.clientSecret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (setupError) {
        setFormError(setupError.message || "Payment setup failed. Please try again.");
        setLoading(false);
        return;
      }

      if (setupIntent.status !== "succeeded") {
        setFormError("Payment method confirmation failed. Please try again.");
        setLoading(false);
        return;
      }

      // Now complete registration with setupIntentId
      const membershipTierKey = activeTier.key || "free";
      const formData = new FormData();
      formData.append("fullName", values.fullName || "");
      formData.append("preferredName", values.preferredName || "");
      formData.append("email", values.email);
      formData.append("password", values.password);
      formData.append("howHeard", values.howHeard || "");
      formData.append("referralEmail", values.referralEmail || "");
      formData.append("hopesForJoining", values.hopesForJoining || "");
      formData.append("membershipTierKey", membershipTierKey);
      formData.append("primaryRoleKey", membershipTierKey);
      formData.append("billingCycle", "monthly");
      formData.append("setupIntentId", setupIntent.id);

      if (profileImageFile) {
        formData.append("pfp", profileImageFile);
      }

      const demographic = {
        dateOfBirth: values.dateOfBirth
          ? values.dateOfBirth.format("YYYY-MM-DD")
          : "",
        locationType: values.locationType || "",
        genderIdentity: values.genderIdentity || "",
        ethnicity: values.ethnicity || "",
        urmIdentification: !!values.urmIdentification,
      };
      formData.append("demographic", JSON.stringify(demographic));

      const response = await fetch("/api/subscriptions/complete-registration", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setFormError("");
        setCurrentStep(2);
        setFormSuccess(data.message || "Payment successful! Welcome to the community!");
        setTimeout(() => navigate("/thankyou"), 2000);
      } else {
        setFormError(data.message || "Registration completion failed. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Payment processing failed. Please try again.";
      setFormError(msg);
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    if (currentStep === 0) {
      handleRegistration(values);
    }
  };

  const labelStyle = {
    fontFamily: "'Cinzel', serif",
    fontWeight: 500,
  };

  return (
    <div style={{ minHeight: "100vh", padding: "60px 0" }}>
      <div className="container" style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Button
          onClick={() => navigate("/")}
          type="text"
          icon={<ArrowLeftOutlined />}
          style={{
            marginBottom: 16,
            color: "black",
            fontWeight: 500,
            fontFamily: "'Cinzel', serif",
          }}
        >
          Back to Home
        </Button>

        {/* Progress Steps for Paid Tiers */}
        {activeTier.monthlyPrice > 0 && (
          <Card style={{ marginBottom: 24, borderRadius: 12 }}>
            <Steps current={currentStep}>
              <Steps.Step title="Registration" icon={<UserOutlined />} />
              <Steps.Step
                title="Payment"
                icon={<CreditCardOutlined />}
                disabled={currentStep < 1}
              />
              <Steps.Step
                title="Complete"
                icon={<CheckCircleOutlined />}
                disabled={currentStep < 2}
              />
            </Steps>
          </Card>
        )}

        <Row gutter={[32, 32]}>
          {/* Left Column - Info */}
          <Col xs={24} lg={10}>
            <Card
              style={{
                borderRadius: 16,
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                border: `1px solid ${colorTheme.border}`,
                background:
                  "linear-gradient(29deg, rgba(0, 240, 251, 1) 0%, rgba(255,255,255,1) 50%, rgba(0, 240, 251, 1) 100%)",
              }}
              bodyStyle={{ padding: 40 }}
            >
              <div style={{ textAlign: "center", marginBottom: 32 }}>
                <Title
                  level={2}
                  style={{
                    color: "black",
                    marginBottom: 8,
                    fontWeight: "500",
                    fontSize: "30px",
                  }}
                  className="font-cinzel"
                >
                  {currentStep === 0 && "Start Your Journey"}
                  {currentStep === 1 && "Secure Payment"}
                  {currentStep === 2 && "Welcome!"}
                </Title>
                <Paragraph
                  style={{
                    fontSize: 16,
                    color: "black",
                    fontFamily: "'Outfit', sans-serif",
                  }}
                >
                  {currentStep === 0 &&
                    "Join our community of passionate photographers"}
                  {currentStep === 1 &&
                    "Complete your payment to activate membership"}
                  {currentStep === 2 && "Your membership is now active!"}
                </Paragraph>
              </div>

              {currentStep === 0 && (
                <>
                  {/* <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <Avatar
                      size={120}
                      icon={!profileImage && <UserOutlined />}
                      src={profileImage}
                      style={{
                        backgroundColor: profileImage ? "transparent" : "black",
                        color: "white",
                        border: "3px solid white",
                        marginBottom: 16,
                      }}
                    />
                    <Upload
                      beforeUpload={() => false}
                      showUploadList={false}
                      onChange={handleImageUpload}
                      accept="image/*"
                    >
                      <Button
                        icon={<EditOutlined />}
                        style={{
                          fontFamily: "'Cinzel', serif",
                        }}
                      >
                        {profileImage ? "Change" : "Upload"} Photo
                      </Button>
                    </Upload>
                  </div> */}

                  <Divider style={{ borderColor: colorTheme.border }} />

                  <div>
                    <Title
                      level={4}
                      style={{ color: "black", marginBottom: 16 }}
                      className="font-cinzel"
                    >
                      <BookOutlined style={{ marginRight: 8 }} />
                      {activeTier.title}
                    </Title>
                    <p style={{ color: "#475569", marginBottom: 20, fontSize: 14 }}>
                      Select the membership tier that best suits your creative journey.
                    </p>

                    {loadingTiers ? (
                      <div style={{ textAlign: "center", padding: 24 }}>
                        <Text>Loading plans...</Text>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 14, marginBottom: 24 }}>
                        {availableTiers.map((tier) => {
                          const isSelected = tier.key === activeTier.key;
                          return (
                            <div
                              key={tier.key}
                              onClick={() => setSelectedTier(tier)}
                              style={{
                                cursor: "pointer",
                                borderRadius: 16,
                                padding: 18,
                                border: isSelected ? "2px solid #0ea5e9" : "1px solid #e2e8f0",
                                backgroundColor: isSelected ? "#f0f9ff" : "white",
                                boxShadow: isSelected ? "0 8px 24px rgba(14,165,233,0.12)" : "0 1px 2px rgba(15,23,42,0.05)",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                  <Text strong style={{ fontSize: 16, color: "#111827" }}>
                                    {tier.title}
                                  </Text>
                                  <div style={{ marginTop: 6, color: "#64748b", fontSize: 13 }}>
                                    ${tier.monthlyPrice}/mo • {tier.yearlyPrice ? `$${tier.yearlyPrice}/yr` : "Free"}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div style={{ color: "#0ea5e9", fontWeight: 700, fontSize: 12 }}>
                                    Selected
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: 8,
                        padding: 18,
                        borderRadius: 14,
                        backgroundColor: "rgba(248,250,252,0.9)",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Text style={{ color: "#475569", fontSize: 14 }}>
                        {activeTier.monthlyPrice > 0
                          ? "Paid members receive full community access, premium galleries, and priority event announcements."
                          : "Free members can explore content, submit a single quest entry, and access community newsletters."}
                      </Text>
                    </div>
                  </div>
                </>
              )}
              {currentStep === 1 && (
                <div
                  style={{
                    padding: 20,
                    backgroundColor: "rgba(255,255,255,0.9)",
                    borderRadius: 12,
                  }}
                >
                  <Title level={4} className="font-cinzel">
                    Order Summary
                  </Title>
                  <Divider style={{ margin: "12px 0" }} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 18,
                    }}
                  >
                    <Text>{activeTier.title} Membership</Text>
                    <Text strong>${activeTier.monthlyPrice}/mo</Text>
                  </div>
                  <Divider style={{ margin: "12px 0" }} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text strong style={{ fontSize: 18 }}>
                      Total Today
                    </Text>
                    <Text strong style={{ fontSize: 18, color: "#0ea5e9" }}>
                      ${activeTier.monthlyPrice}
                    </Text>
                  </div>
                  <Text
                    type="secondary"
                    style={{
                      display: "block",
                      marginTop: 12,
                      fontSize: 12,
                    }}
                  >
                    Billing renews monthly. Cancel anytime.
                  </Text>
                </div>
              )}
            </Card>
          </Col>

          {/* Right Column - Form */}
          <Col xs={24} lg={14}>
            <Card
              style={{
                borderRadius: 16,
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                border: `1px solid ${colorTheme.border}`,
              }}
              bodyStyle={{ padding: 40 }}
            >
              {/* STEP 0: Registration Form */}
              {currentStep === 0 && (
                <>
                  <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <Title
                      level={3}
                      style={{
                        color: "black",
                        fontWeight: "500",
                        fontSize: "30px",
                      }}
                      className="font-cinzel"
                    >
                      Create Your Profile
                    </Title>
                    <Text style={{ color: "black" }}>
                      Fill in your details to get started
                    </Text>
                  </div>

                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    requiredMark={false}
                    scrollToFirstError
                  >
                    <Form.Item
                      labelCol={{ style: labelStyle }}
                      label="Preferred Name"
                      name="preferredName"
                      extra="How you prefer to be addressed"
                    >
                      <Input
                        size="large"
                        placeholder="Enter preferred name"
                        style={{
                          borderRadius: 8,
                          fontFamily: "'Cinzel', serif",
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      labelCol={{ style: labelStyle }}
                      label="Full Name (For member id)"
                      name="fullName"
                      rules={[{ required: true, message: "Full name required" }]}
                    >
                      <Input
                        size="large"
                        placeholder="Enter full name"
                        style={{
                          borderRadius: 8,
                          fontFamily: "'Cinzel', serif",
                        }}
                      />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          labelCol={{ style: labelStyle }}
                          label="Email"
                          name="email"
                          rules={[
                            {
                              required: true,
                              type: "email",
                              message: "Valid email required",
                            },
                          ]}
                        >
                          <Input
                            size="large"
                            placeholder="your@email.com"
                            style={{ borderRadius: 8 }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          labelCol={{ style: labelStyle }}
                          label="Password"
                          name="password"
                          rules={[
                            { required: true, message: "Password required" },
                            { min: 6, message: "Min 6 characters" },
                          ]}
                        >
                          <Input.Password
                            size="large"
                            placeholder="Create password"
                            style={{ borderRadius: 8 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      labelCol={{ style: labelStyle }}
                      label="Confirm Password"
                      name="confirmPassword"
                      dependencies={["password"]}
                      rules={[
                        { required: true, message: "Confirm password" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("Passwords do not match")
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        size="large"
                        placeholder="Re-enter password"
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>

                    <Divider orientation="left">
                      <Text strong style={{ fontFamily: "'Cinzel', serif" }}>
                        Community Details
                      </Text>
                    </Divider>

                    <Form.Item
                      labelCol={{ style: labelStyle }}
                      label="How did you hear about us?"
                      name="howHeard"
                      rules={[{ required: true, message: "Please select" }]}
                    >
                      <Select size="large" placeholder="Select an option">
                        {howHeardOptions.map((option) => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {needsReferralEmail && (
                      <Form.Item
                        labelCol={{ style: labelStyle }}
                        label="Friend / Colleague Email"
                        name="referralEmail"
                        rules={[
                          { required: true, message: "Please enter the email" },
                          { type: "email", message: "Please enter a valid email address" },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="friend@example.com"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    )}

                    <Form.Item
                      labelCol={{ style: labelStyle }}
                      label="Hopes for Joining"
                      name="hopesForJoining"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="What do you hope to achieve..."
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>

                    <Divider orientation="left">
                      <Text strong style={{ fontFamily: "'Cinzel', serif" }}>
                        Demographics (Optional)
                      </Text>
                    </Divider>

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          labelCol={{ style: labelStyle }}
                          label="Year Of Birth"
                          name="dateOfBirth"
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <DatePicker
                            size="large"
                            picker="year"
                            placeholder="Select birth year"
                            style={{ width: "100%", borderRadius: 8 }}
                            disabledDate={(current) =>
                              current &&
                              (current.year() > new Date().getFullYear() ||
                                current.year() < 1900)
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          labelCol={{ style: labelStyle }}
                          label="Location Type"
                          name="locationType"
                        >
                          <Select size="large" placeholder="Select">
                            {locationTypes.map((loc) => (
                              <Option key={loc} value={loc}>
                                {loc.charAt(0).toUpperCase() + loc.slice(1)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          labelCol={{ style: labelStyle }}
                          label="Gender Identity"
                          name="genderIdentity"
                        >
                          <Select size="large" placeholder="Select">
                            {genderIdentities.map((gender) => (
                              <Option key={gender} value={gender}>
                                {gender
                                  .split("-")
                                  .map(
                                    (w) =>
                                      w.charAt(0).toUpperCase() + w.slice(1)
                                  )
                                  .join(" ")}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          labelCol={{ style: labelStyle }}
                          label="Ethnicity"
                          name="ethnicity"
                        >
                          <Select size="large" placeholder="Select">
                            {ethnicities.map((eth) => (
                              <Option key={eth} value={eth}>
                                {eth.charAt(0).toUpperCase() + eth.slice(1)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item name="urmIdentification" valuePropName="checked">
                      <Checkbox>
                        I identify as part of an Underrepresented Minority (URM)
                      </Checkbox>
                    </Form.Item>

                    {/* Inline error/success alerts */}
                    {formError && (
                      <div style={{
                        marginBottom: 16,
                        padding: "12px 16px",
                        borderRadius: 10,
                        backgroundColor: "#fff1f0",
                        border: "1px solid #ffa39e",
                        color: "#cf1322",
                        fontFamily: "'Outfit', sans-serif",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}>
                        <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
                        <span>{formError}</span>
                      </div>
                    )}
                    {formSuccess && (
                      <div style={{
                        marginBottom: 16,
                        padding: "12px 16px",
                        borderRadius: 10,
                        backgroundColor: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        color: "#389e0d",
                        fontFamily: "'Outfit', sans-serif",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}>
                        <span style={{ fontSize: 16, flexShrink: 0 }}>✓</span>
                        <span>{formSuccess}</span>
                      </div>
                    )}
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        loading={loading}
                        style={{
                          backgroundColor: "black",
                          borderRadius: 8,
                          height: 48,
                          width: "100%",
                          fontWeight: 600,
                          fontSize: 16,
                        }}
                        className="font-cinzel"
                      >
                        {activeTier.monthlyPrice > 0
                          ? "Continue to Payment"
                          : "Create Profile"}
                      </Button>
                    </Form.Item>
                  </Form>
                </>
              )}

              {/* STEP 1: Payment Form */}
              {currentStep === 1 && (
                <>
                  <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <Title level={3} style={{ color: "black", fontWeight: "500", fontSize: "30px" }} className="font-cinzel">
                      Complete Payment
                    </Title>
                    <Text style={{ color: "black" }}>Secure payment powered by Stripe</Text>
                  </div>

                  {/* Inline error for payment step */}
                  {formError && (
                    <div style={{
                      marginBottom: 20,
                      padding: "12px 16px",
                      borderRadius: 10,
                      backgroundColor: "#fff1f0",
                      border: "1px solid #ffa39e",
                      color: "#cf1322",
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: 14,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                    }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
                      <span>{formError}</span>
                    </div>
                  )}

                  <div
                    style={{
                      padding: 20,
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      marginBottom: 24,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 16,
                        fontFamily: "'Cinzel', serif",
                        fontSize: 16,
                      }}
                    >
                      <CreditCardOutlined style={{ marginRight: 8 }} />
                      Card Information
                    </Text>
                    <div
                      style={{
                        padding: 16,
                        backgroundColor: "white",
                        borderRadius: 8,
                        border: "1px solid #d9d9d9",
                      }}
                    >
                      <CardElement
                        options={CARD_ELEMENT_OPTIONS}
                        onChange={handleCardChange}
                      />
                    </div>
                    {cardError && (
                      <Text
                        type="danger"
                        style={{ display: "block", marginTop: 8 }}
                      >
                        {cardError}
                      </Text>
                    )}
                  </div>

                  <div
                    style={{
                      padding: 12,
                      backgroundColor: "#fffbeb",
                      borderRadius: 8,
                      marginBottom: 24,
                      border: "1px solid #fde68a",
                    }}
                  >
                    <Text style={{ fontSize: 13, color: "#92400e" }}>
                      💡 Test: 4242 4242 4242 4242 | Any future date | Any CVC
                    </Text>
                  </div>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Button
                        size="large"
                        block
                        onClick={() => setCurrentStep(0)}
                        disabled={loading}
                        style={{
                          height: 48,
                          fontFamily: "'Cinzel', serif",
                        }}
                      >
                        Back
                      </Button>
                    </Col>
                    <Col span={12}>
                      <Button
                        type="primary"
                        size="large"
                        block
                        onClick={handlePayment}
                        loading={loading}
                        disabled={!cardComplete || loading}
                        style={{
                          backgroundColor: "black",
                          height: 48,
                          fontFamily: "'Cinzel', serif",
                        }}
                      >
                        {loading ? "Processing..." : "Pay Now"}
                      </Button>
                    </Col>
                  </Row>
                </>
              )}

              {/* STEP 2: Success */}
              {currentStep === 2 && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <CheckCircleOutlined
                    style={{ fontSize: 64, color: "#52c41a", marginBottom: 24 }}
                  />
                  <Title level={2} className="font-cinzel">
                    Payment Successful!
                  </Title>
                  <Paragraph style={{ fontSize: 16, marginBottom: 32 }}>
                    Your membership has been activated. Welcome to the
                    community!
                  </Paragraph>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate("/dashboard")}
                    style={{
                      backgroundColor: "black",
                      height: 48,
                      width: "200px",
                    }}
                  >
                    Go to Dashboard
                  </Button>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

// =====================
// MAIN REGISTER COMPONENT (WRAPPER)
// =====================
const Register = () => {
  const { state } = useLocation();
  const { id, key, title, points, monthlyPrice,yearlyPrice} = state || {};

  const tierInfo = {
    id,
    key,
    title: title || "Membership",
    points: points || [],
    monthlyPrice: monthlyPrice || 0,
    yearlyPrice: yearlyPrice || 0,
  };

  if (!stripePromise) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Text type="danger">
          Payment system configuration error. Please contact support.
        </Text>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <RegisterFormWithPayment tierInfo={tierInfo} />
    </Elements>
  );
};

export default Register;
